import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _, { debounce } from 'lodash';
import { withRouter } from 'react-router-dom';
import { withTranslation, useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import {
  fetchConditionsByTerms,
  fetchDrugClassesByTerms,
  fetchSymptomsCausesByTerms
} from '../../../services/Api/Conditions/ConditionSevice';
import SearchDropdown from './SearchDropdown/index.js';
import { CONDITION_TYPE } from '../../../constants/defaults';

const CommonSearchBar = props => {
  let {
    shouldFocus,
    classes,
    hideTextSearch,
    showMobileSearchBar,
    type,
    inputPlaceHolder
  } = props;

  const [drug, setDrug] = useState('');
  const [drugSearchResults, setDrugSearchResults] = useState([]);
  const [drugSuggestionDropdown, setDrugSuggestionDropdown] = useState(false);
  const [noDrugFound, setNoDrugFound] = useState(false);
  const [showDummyLoader, setShowDummyLoader] = useState(false);
  const [submitId, setSubmitId] = useState(0);
  const { t } = useTranslation();

  const getDrugs = async searchTerm => {
    if (type === 'condition') {
      fetchConditionsByTerms(searchTerm)
        .then(res => {
          const drugSearchResults = res.conditions;
          if (!_.isEmpty(drugSearchResults)) {
            setDrugSearchResults(drugSearchResults);
            setDrugSuggestionDropdown(true);
            setNoDrugFound(false);
          } else {
            setDrugSearchResults(drugSearchResults);
            setDrugSuggestionDropdown(false);
            setNoDrugFound(true);
          }
        })
        .catch(() => {
          setDrugSearchResults([]);
          setDrugSuggestionDropdown(false);
          setNoDrugFound(true);
        });
    } else if (type === 'drug') {
      fetchDrugClassesByTerms(searchTerm)
        .then(res => {
          let drugSearchResults = res.drugClass;
          if (!_.isEmpty(drugSearchResults)) {
            setDrugSearchResults(drugSearchResults);
            setDrugSuggestionDropdown(true);
            setNoDrugFound(false);
          } else {
            setDrugSearchResults(drugSearchResults);
            setDrugSuggestionDropdown(false);
            setNoDrugFound(true);
          }
        })
        .catch(() => {
          setDrugSearchResults([]);
          setDrugSuggestionDropdown(false);
          setNoDrugFound(true);
        });
    } else if (
      type === CONDITION_TYPE.CAUSE ||
      type === CONDITION_TYPE.SYMPTOM
    ) {
      fetchSymptomsCausesByTerms(searchTerm, type)
        .then(res => {
          let drugSearchResults = res.symptomsCauses;
          if (!_.isEmpty(drugSearchResults)) {
            setDrugSearchResults(drugSearchResults);
            setDrugSuggestionDropdown(true);
            setNoDrugFound(false);
          } else {
            setDrugSearchResults(drugSearchResults);
            setDrugSuggestionDropdown(false);
            setNoDrugFound(true);
          }
        })
        .catch(() => {
          setDrugSearchResults([]);
          setDrugSuggestionDropdown(false);
          setNoDrugFound(true);
        });
    }
  };

  const blurHandlerForSearchBar = () => {
    setDrugSuggestionDropdown(false);
  };

  const getDrugsDelayedCallback = debounce(getDrugs, 500);
  const getonBlurHandlerForSearchBarCallback = debounce(
    blurHandlerForSearchBar,
    250
  );

  const searchDrug = (event = { preventDefault() {} }) => {
    const { value } = event.target;
    if (value.length === 0) {
      setDrugSuggestionDropdown(false);
      setDrug(value);
      return;
    } else {
      setDrug(value);
      if (value.length >= 3) getDrugsDelayedCallback(value);
    }
  };

  const searchDrugFocusHandle = (event = { preventDefault() {} }) => {
    event.preventDefault();
    if (_.isEmpty(drug)) {
      return;
    }
  };

  const onBlurHandler = () => {
    setNoDrugFound(false);
    getonBlurHandlerForSearchBarCallback();
  };

  const handleSubmit = (event = { preventDefault() {} }) => {
    event.preventDefault();
    if (drug.length === 0) {
      setDrugSuggestionDropdown(false);
      return;
    } else {
      if (drug.length >= 3) {
        setShowDummyLoader(true);
        getDrugsDelayedCallback(drug);
        setTimeout(() => {
          setShowDummyLoader(false);
        }, 850);
      }
    }
  };

  const addSubmitId = submitId => {
    setSubmitId(submitId);
  };

  return (
    <div>
      <section className={`${classes ? classes : 'search-masthead'}`}>
        <div
          id="vm-search"
          className="site-search open"
          role="search"
          aria-label={t('Search by condition')}
        >
          <div className="valid">
            <div className={'site-search-form search-center'}>
              {!hideTextSearch && (
                <form
                  autoComplete="off"
                  className={
                    showMobileSearchBar
                      ? 'search-form-wd-false'
                      : 'search-form-wd'
                  }
                  onSubmit={handleSubmit}
                >
                  <div
                    className={`site-search-form--query ${
                      showDummyLoader && 'active'
                    }`}
                  >
                    <div className="fullwidth position-relative">
                      <span className="sc-icon desktop-only">
                        <LazyLoadImage
                          src="/content/images/conditions/search.png"
                          alt=""
                        />{' '}
                      </span>
                      <input
                        className={`${
                          showDummyLoader && 'active'
                        } sc-form__text site-search-form--term whiteFocus`}
                        id="searchbar"
                        placeholder={
                          inputPlaceHolder ? inputPlaceHolder : 'search'
                        }
                        value={drug}
                        onChange={searchDrug}
                        onFocus={searchDrugFocusHandle}
                        onBlur={onBlurHandler}
                        autoComplete="off"
                        type="search"
                        autoFocus={shouldFocus}
                        aria-label={
                          inputPlaceHolder ? inputPlaceHolder : 'search'
                        }
                        aria-required="true"
                        aria-controls="site-search-results"
                        aria-autocomplete="list"
                        aria-activedescendant={`option-${submitId}`}
                      />
                      {!_.isEmpty(drug) && (
                        <button
                          onBlur={onBlurHandler}
                          className="closeSearch"
                          onClick={() => setDrug('')}
                          aria-label={t('Clear search text')}
                        >
                          <img
                            src="/content/images/icons/search-close-icon.png"
                            alt=""
                          />
                        </button>
                      )}
                    </div>
                    {showDummyLoader ? (
                      <img
                        src="/content/images/loader/loading.gif"
                        alt="loader"
                        className="searchBarLoader"
                      />
                    ) : (
                      <button
                        type="submit"
                        value="Search"
                        className="search-btn sc-outline-btn--cool-gray desktop-only"
                      >
                        <span className="desktop-only">Search</span>
                        <LazyLoadImage
                          src="/content/images/icons/search-icon.svg"
                          alt=""
                          className="mobile-only"
                        />
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>

          <SearchDropdown
            listItems={drugSearchResults}
            drugSuggestionDropdown={drugSuggestionDropdown}
            noDrugFound={noDrugFound}
            drug={drug}
            submitId={submitId}
            setSubmitId={addSubmitId}
            type={type}
          />
        </div>
      </section>
    </div>
  );
};

CommonSearchBar.propTypes = {
  showMobileSearchBar: PropTypes.bool,
  handleSearchSubmitOverLay: PropTypes.func,
  classes: PropTypes.string
};

CommonSearchBar.defaultProps = {
  showMobileSearchBar: false,
  classes: ''
};

export default withRouter(withTranslation()(CommonSearchBar));
